<script lang="ts">
  import { api } from "../api/client";
  import { push } from "svelte-spa-router";
  import ButtonSmall from "../components/ButtonSmall.svelte";
  import LoadingSpinner from "../components/LoadingSpinner.svelte";
  import RecipeCard from "../components/RecipeCard.svelte";
  import RecipeCosting from "../components/RecipeCosting.svelte";
  import RecipeLinking from "../components/RecipeLinking.svelte";
  import Text from "../components/Text.svelte";
  import { WHITELISTED_FOR_RECIPE_COSTING } from "../helpers/constants";
  import { store } from "../helpers/store";
  import type { DatabaseMatch, Recipe, RecipeFull, RecipeWithFresh } from "../helpers/types";
  import { selectedBuyer } from "../stores/buyer";

  let recipes: RecipeFull[] = [];
  let modified: RecipeWithFresh[] = [];
  let searchIndex: Map<string, Recipe> = new Map();
  let loading = false;
  let saving: boolean = false;
  let recipesLinking: Recipe[] = [];
  let allIngredientToRecipes: Record<string, Recipe[]> = {};
  let connectedLines: DatabaseMatch[] = [];
  let percentIngredientsLinkedForRecipe: Record<string, number> = {};

  let emailCopySuccess: boolean = false;

  let recipeCosting: any;

  $: buyer = $selectedBuyer;

  $: {
    if (buyer && !WHITELISTED_FOR_RECIPE_COSTING.includes(buyer.id)) {
      push("/");
    }
  }

  const getConnectedLines = async () => {
    try {
      const body = {
        buyer_id: buyer?.id,
        invoice_lines: [],
      };
      const result = await api
        .post("link-invoice-recipes", { body: JSON.stringify(body) })
        .json<DatabaseMatch[]>();
      return result;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const fetchRecipes = async () => {
    try {
      const body = {
        buyer_id: buyer?.id,
      };
      const { recipes } = await api.post("recipes", { body: JSON.stringify(body) }).json<any>();
      return recipes;
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSearchIndex = async (): Promise<Map<string, Recipe>> => {
    try {
      let searchIndex: Map<string, Recipe> = new Map();
      const body = {
        buyer_id: buyer?.id,
      };
      const response = await api.post("recipes-and-ingredients", { body: JSON.stringify(body) });
      const {
        search_index,
      }: {
        search_index: { [key: string]: Recipe };
      } = await response.json();

      Object.entries(search_index).forEach(([key, recipe]) => {
        searchIndex.set(key, recipe as Recipe); // Cast to Recipe if necessary
      });

      return searchIndex;
    } catch (e) {
      console.error(e);
      return new Map();
    }
  };

  let overflowHidden = false;

  const addRecipe = () => {
    modified = [
      {
        fresh: true,
        name: "",
        buyer_id: buyer?.id ?? "Invalid buyer id",
        items: [],
        type: "recipe",
      },
      ...modified,
    ];
  };

  const saveRecipe = async (recipe: Recipe) => {
    saving = true;
    try {
      const body = {
        buyer_id: buyer?.id,
        recipe,
      };
      const { recipe: savedRecipe } = await api
        .post("recipe", { body: JSON.stringify(body) })
        .json<any>();
      const index = modified.findIndex((r) => r.id === recipe.id);
      modified[index] = savedRecipe;
      modified = [...modified];
      recipes = JSON.parse(JSON.stringify(modified.filter((r) => r.id)));
      saving = false;
      await reloadSearchIndex();
      collectIngredients(savedRecipe, savedRecipe.id);
      await reloadLinks();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteRecipe = async (recipeId: string) => {
    try {
      const body = {
        buyer_id: buyer?.id,
        recipe_id: recipeId,
      };
      await api.post("recipe/delete", { body: JSON.stringify(body) }).json<any>();
    } catch (e) {
      console.error(e);
    }
  };

  const searchInIndex = (query: string, exclude: string[]): Recipe[] => {
    console.log("searching in index", query, exclude);
    const results: Recipe[] = [];
    for (const key of searchIndex.keys()) {
      const ingredient = searchIndex.get(key);
      if (
        ingredient &&
        ingredient.name.toLowerCase().includes(query.toLowerCase()) &&
        ingredient.id &&
        (exclude ? !exclude.includes(ingredient.id) : true)
      ) {
        results.push(ingredient);
      }
    }
    return results;
  };

  function collectIngredients(recipe: Recipe, mainRecipeId: string | undefined) {
    if (recipe.items === undefined) return;
    if (!mainRecipeId) mainRecipeId = recipe.id;
    for (const ingredient of recipe.items) {
      if (ingredient.type === "recipe") {
        collectIngredients(ingredient, mainRecipeId);
      } else {
        if (!allIngredientToRecipes[ingredient.name]) allIngredientToRecipes[ingredient.name] = [];

        const recipe = recipes.find((r) => r.id === mainRecipeId);
        if (recipe?.name) allIngredientToRecipes[ingredient.name].push(recipe);
      }
    }
  }

  function emailCopy(text: string) {
    navigator.clipboard.writeText(text);
    emailCopySuccess = true;
    setTimeout(() => {
      emailCopySuccess = false;
    }, 2000);
  }

  async function reloadSearchIndex() {
    searchIndex = await fetchSearchIndex();
  }

  async function reloadLinks() {
    connectedLines = await getConnectedLines();
    percentIngredientsLinkedForRecipe = {};

    let recipeIngredientsTotal: Record<string, string[]> = {};
    Object.keys(allIngredientToRecipes).forEach((ingredient) => {
      let recipesForIngredient = allIngredientToRecipes[ingredient];
      recipesForIngredient.forEach((r) => {
        if (r.id) {
          if (!recipeIngredientsTotal[r.id]) recipeIngredientsTotal[r.id] = [];
          if (!recipeIngredientsTotal[r.id].includes(ingredient))
            recipeIngredientsTotal[r.id].push(ingredient);
        }
      });
    });
    Object.keys(recipeIngredientsTotal).forEach((recipeId) => {
      let ingredients = recipeIngredientsTotal[recipeId];
      let connectedLinesForRecipeWithIngredients = connectedLines.filter((line) =>
        ingredients.includes(line.recipe_ingredient_item_name)
      );
      let percentIngredientsLinked =
        connectedLinesForRecipeWithIngredients.length / ingredients.length;
      percentIngredientsLinkedForRecipe[recipeId] = percentIngredientsLinked;
    });
  }

  async function initRecipes() {
    loading = true;
    let recipesSaved = await fetchRecipes();
    recipes = recipesSaved ? JSON.parse(JSON.stringify(recipesSaved)) : [];
    modified = JSON.parse(JSON.stringify(recipes));

    for (const recipe of recipes) {
      collectIngredients(recipe, recipe.id);
    }

    loading = false;

    await reloadSearchIndex();
    await reloadLinks();
  }

  $: {
    if (buyer && !modified.find((r) => !r.id)) {
      addRecipe();
    }
  }

  $: {
    if (buyer) {
      initRecipes();
    }
  }
  $: isDirty = (recipe: RecipeWithFresh, index: number = -1) => {
    let original: Recipe | undefined;
    if (recipe.fresh && recipe.name !== "") {
      return true;
    }
    if (recipe.id) {
      original = recipes.find((r) => r.id === recipe.id);
    } else if (index >= 0) {
      original = recipes[index];
    }
    if (!original) return false;
    return JSON.stringify(original) !== JSON.stringify(recipe);
  };
</script>

<div class="container">
  {#if loading}
    <div class="loading-container">
      <LoadingSpinner size={60} />
    </div>
  {:else}
    <div class="page-content">
      <div class="page-content-header">
        <Text size="large" bold>recipes</Text>
      </div>
      <ButtonSmall on:click={() => recipeCosting.open()}>calculate recipes costing</ButtonSmall>

      <!-- <div class="new-recipe"> -->
      <!-- <Button stretch on:click={addRecipe}>add new recipe</Button> -->
      <!-- </div> -->
      <div class="recipes-list">
        {#each modified as recipe, index (recipe.id ? recipe.id : "fresh")}
          {#if !recipe.is_sub_recipe && (recipe.id || recipe.fresh)}
            <RecipeCard
              {recipe}
              {buyer}
              fresh={!recipe.id}
              dirty={isDirty(recipe, index)}
              percentLinked={recipe.id ? percentIngredientsLinkedForRecipe[recipe.id] : null}
              {saving}
              onSearch={(query, recipeItems) => Promise.resolve(searchInIndex(query, recipeItems))}
              on:update={(e) => {
                modified[index] = e.detail.recipe;
                modified = [...modified];
              }}
              on:save={(e) => {
                saveRecipe(e.detail.recipe);
              }}
              on:link={(e) => {
                recipesLinking = [e.detail.recipe];
              }}
              on:delete={() => {
                const index = modified.findIndex((r) => r === recipe);
                if (recipe.id) deleteRecipe(recipe.id);
                modified.splice(index, 1);
                recipes = JSON.parse(JSON.stringify(modified.filter((r) => r.id)));
                // modified = [...modified];
              }}
            />
          {/if}
        {/each}
      </div>
    </div>
  {/if}
</div>

{#if recipesLinking.length}
  <RecipeLinking
    recipes={recipesLinking}
    {allIngredientToRecipes}
    {buyer}
    {searchIndex}
    on:close={() => (recipesLinking = [])}
    on:relink={() => {
      reloadLinks();
    }}
  />
{/if}

<RecipeCosting
  bind:this={recipeCosting}
  {buyer}
  {recipes}
  {searchIndex}
  {percentIngredientsLinkedForRecipe}
  databaseConnectedLines={connectedLines}
/>

<svelte:body class:overflowHidden />

<style>
  .container {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
    height: 100vh;
    padding: 0px 15px;
    overflow: auto;
  }

  .page-content-header {
    display: flex;
    align-items: center;
  }

  .page-content {
    padding: 20px 0;
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: auto;
    gap: 20px;
  }

  .page-content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
  }

  .new-recipe {
    flex: 0;
  }

  .recipes-list {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 10px;
    padding-bottom: 20px;
  }
</style>
